import React, { useMemo } from "react";
import {
	Autocomplete,
	Checkbox,
	Chip,
	FormControl,
	FormHelperText,
	TextField,
	Typography,
} from "@mui/material";

const normalizeSearchText = (value = "") =>
	String(value ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

export const ClienteMultiSelect = ({
	clientes = [],
	value = [],
	onChange = () => {},
	label = "Clientes asignados",
	disabled = false,
	helperText = "",
}) => {
	const clientesValidos = useMemo(
		() =>
			Array.isArray(clientes)
				? clientes.filter(
						(cliente) =>
							cliente &&
							Number.isFinite(Number(cliente.id)) &&
							Number(cliente.id) > 0
				  )
				: [],
		[clientes]
	);

	const selectedIds = useMemo(() => {
		if (!Array.isArray(value)) {
			return [];
		}

		return [
			...new Set(
				value
					.map((item) => {
						// Permite recibir tanto IDs como objetos de cliente
						const id =
							typeof item === "object" && item !== null
								? item.id
								: item;

						return Number(id);
					})
					.filter((id) => Number.isFinite(id) && id > 0)
			),
		];
	}, [value]);

	const selectedSet = useMemo(
		() => new Set(selectedIds),
		[selectedIds]
	);

	const selectedClientes = useMemo(
		() =>
			clientesValidos.filter((cliente) =>
				selectedSet.has(Number(cliente.id))
			),
		[clientesValidos, selectedSet]
	);

	return (
		<FormControl fullWidth disabled={disabled}>
			<Autocomplete
				multiple
				disableCloseOnSelect
				disabled={disabled}
				options={clientesValidos}
				value={selectedClientes}
				isOptionEqualToValue={(option, selected) =>
					Number(option?.id) === Number(selected?.id)
				}
				getOptionLabel={(option) =>
					option?.nombre?.trim() ||
					`Cliente ${option?.id ?? ""}`
				}
				filterOptions={(options, state) => {
					const query = normalizeSearchText(
						state?.inputValue
					);

					if (!query) {
						return options;
					}

					return options.filter((option) => {
						const searchableText = normalizeSearchText(
							[
								option?.nombre,
								option?.id,
								option?.id_clienteG,
								option?.ref_cliente_maestro,
							]
								.filter(
									(value) =>
										value !== undefined &&
										value !== null &&
										value !== ""
								)
								.join(" ")
						);

						return searchableText.includes(query);
					});
				}}
				onChange={(_, nextValue) => {
					const nextIds = [
						...new Set(
							nextValue
								.map((item) => Number(item?.id))
								.filter(
									(id) =>
										Number.isFinite(id) &&
										id > 0
								)
						),
					];

					onChange(nextIds);
				}}
				renderOption={(props, option, { selected }) => {
					const secondaryText = option?.id_clienteG
						? `ClienteG ${option.id_clienteG}`
						: option?.ref_cliente_maestro || "";

					return (
						<li {...props} key={`cliente-option-${option.id}`}>
							<Checkbox
								checked={selected}
								sx={{ mr: 1 }}
							/>

							<div>
								<Typography variant="body1">
									{option?.nombre?.trim() ||
										`Cliente ${option.id}`}
								</Typography>

								{secondaryText && (
									<Typography
										variant="caption"
										color="text.secondary"
									>
										{secondaryText}
									</Typography>
								)}
							</div>
						</li>
					);
				}}
				renderTags={(selectedOptions, getTagProps) =>
					selectedOptions.map((option, index) => {
						const { key, ...tagProps } = getTagProps({
							index,
						});

						return (
							<Chip
								{...tagProps}
								key={
									key ||
									`cliente-chip-${option.id}`
								}
								size="small"
								label={
									option?.nombre?.trim() ||
									`Cliente ${option.id}`
								}
							/>
						);
					})
				}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						placeholder={
							selectedClientes.length === 0
								? "Buscar clientes..."
								: ""
						}
					/>
				)}
				noOptionsText="No hay clientes"
			/>

			{helperText && (
				<FormHelperText>{helperText}</FormHelperText>
			)}
		</FormControl>
	);
};

export default ClienteMultiSelect;